/**
 * A school billing account's insufficient-funds block: the shortfall, the
 * top-up incentive, and an "Add funds" button opening the school TopUpSheet,
 * with a balance watch that lifts the gate once the credit lands. Shared by
 * school checkout and the order-for-a-student dialog.
 */
import { DEFAULT_MIN_TOP_UP_CENTS } from '@ordo/constants/wallet'
import { Button } from '@ordo/ui/button'
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react'

import {
  createSchoolTestTopUp,
  createSchoolTopUpIntent,
  type SchoolBillingAccount,
  schoolBillingAccountQueryKey,
} from '@/api/billing-school-account'
import type { AdminSchool } from '@/api/school-orders'
import { TopUpSheet, type TopUpSheetAccount } from '@/components/billing/top-up-sheet'
import { useSchoolTopUpWatch } from '@/components/billing/use-school-top-up-watch'
import { effectiveTopUpIncentiveBps, topUpPrefillCents } from '@/lib/school-orders/funds'
import { formatCents } from '@/lib/school-orders/money'

/**
 * One gated billing account's insufficient-funds block in the All-view
 * rail — the single-campus block, labeled with the campuses that debit
 * the account, with its own reused TopUpSheet + balance watch bound to
 * the account's first campus (any campus on the account authorizes the
 * school-scoped top-up endpoints).
 */
export function CampusTopUpBlock({
  school,
  campusNames,
  account,
  shortfallCents,
  onBalanceMaybeChanged,
  onTopUpOpened,
  showShortfall = true,
}: {
  school: AdminSchool
  campusNames: string[]
  account: SchoolBillingAccount
  shortfallCents: number
  showShortfall?: boolean
  /** Refresh every scope balance read (campuses can share the account). */
  onBalanceMaybeChanged: () => void
  /** The top-up sheet opened — the caller's analytics hook, if it has one. */
  onTopUpOpened?: () => void
}): ReactElement {
  const [topUpOpen, setTopUpOpen] = useState(false)
  const topUpWatch = useSchoolTopUpWatch(school.id)
  // The watch polls only THIS campus's balance key; once it settles, the
  // other campuses on the account still hold the stale snapshot — refresh
  // the whole scope so the gate lifts everywhere at once.
  const watchWasPending = useRef(false)
  useEffect(() => {
    if (watchWasPending.current && !topUpWatch.pending) {
      onBalanceMaybeChanged()
    }
    watchWasPending.current = topUpWatch.pending
  }, [topUpWatch.pending, onBalanceMaybeChanged])

  const incentiveBps = effectiveTopUpIncentiveBps(account)
  const topUpIncentiveCopy =
    incentiveBps > 0
      ? `Added funds earn a ${(incentiveBps / 100).toFixed(2).replace(/\.?0+$/, '')}% bonus credit`
      : null
  const minimumToOrderCopy = `Minimum to place order is ${formatCents(shortfallCents, account.currency)}`
  const sheetAccount = useMemo<TopUpSheetAccount>(
    () => ({
      id: account.accountId,
      currency: account.currency,
      // A school setting a funding floor on its own wallet is self-referential,
      // so ORGANIZATION accounts stay on the platform default — PROD-1317
      // scopes the configurable minimum to FAMILY wallets.
      minTopUpCents: DEFAULT_MIN_TOP_UP_CENTS,
      paymentMethods: account.paymentMethods,
      defaultPaymentMethodRef: account.defaultPaymentMethodRef,
    }),
    [account],
  )

  function handleTopUpOpenChange(open: boolean): void {
    if (open) {
      onTopUpOpened?.()
    }
    setTopUpOpen(open)
    if (!open) {
      // Local test top-ups credit synchronously without firing onPaid —
      // refresh on close so the gate reflects the new balance immediately.
      onBalanceMaybeChanged()
    }
  }

  return (
    <>
      <div
        className={
          showShortfall ? 'rounded-md border border-destructive/30 bg-destructive/5 p-3' : 'rounded-md border p-3'
        }>
        {showShortfall ? <p className="text-sm font-medium text-destructive">Insufficient funds</p> : null}
        {showShortfall ? (
          <p className="mt-1 text-xs text-destructive">
            {campusNames.join(', ')}. {minimumToOrderCopy}.
          </p>
        ) : null}
        {topUpIncentiveCopy ? (
          <p className="mt-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">{topUpIncentiveCopy}.</p>
        ) : null}
        <Button type="button" onClick={() => setTopUpOpen(true)} disabled={topUpWatch.pending} className="mt-3 w-full">
          {topUpWatch.pending ? 'Updating balance…' : 'Add funds'}
        </Button>
        {topUpWatch.stillProcessing ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Your payment is still processing. These campuses rejoin the order as soon as the funds land.
          </p>
        ) : null}
      </div>
      <TopUpSheet
        open={topUpOpen}
        onOpenChange={handleTopUpOpenChange}
        account={sheetAccount}
        api={{
          createTopUpIntent: (vars) => createSchoolTopUpIntent({ ...vars, schoolLocationId: school.id }),
          createTestTopUp: ({ amountCents }) => createSchoolTestTopUp({ schoolLocationId: school.id, amountCents }),
          testTopUpInvalidateKey: schoolBillingAccountQueryKey(school.id),
        }}
        initialAmountCents={topUpPrefillCents(shortfallCents, sheetAccount.minTopUpCents)}
        amountHelperText={showShortfall ? minimumToOrderCopy : undefined}
        incentiveBps={incentiveBps}
        paymentMethodBadge="Organization payment method"
        onPaid={({ amountCents }) => topUpWatch.watch({ amountCents })}
      />
    </>
  )
}
